/**
 * QR-1b: страница владельца «Мой QR» (тикет QR-1 §4) — маршрут /msg/invite
 * в PrivateRoutes, вкладка в SettingsNav; гейт — msgInvites из /me
 * (сервер отдаёт messenger_v2 && msg_invites_enabled; флаг off -> «раздел
 * недоступен», ссылок на него тоже нет).
 *
 * Что тут: подпись -> «Создать QR» -> QR (библиотека qrcode, canvas ->
 * data-URL; CSP img-src 'self' data: уже разрешает) + ссылка, «Копировать»,
 * «Скачать PNG», «Выпустить новый» (старый QR умрёт, участники останутся),
 * «Отозвать». Ниже — участники: имя, дата, «Чат» (/msg/m/:id), «Удалить».
 * Подтверждения — inline (второй клик), без window.confirm.
 *
 * Ссылка приглашения: сервер отдаёт url (PUBLIC_BASE_URL + '/j/#' + token);
 * если PUBLIC_BASE_URL не задан (url null) — строим от своего origin той же
 * формой (inviteLink.inviteUrl). Токен — ТОЛЬКО во фрагменте (§3.3).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import CabinetShell from '../../components/UI/CabinetShell/cabinetShell';
import SettingsNav from '../../components/UI/SettingsNav/settingsNav';
import { useAuth } from '../../context/AuthContext';
import {
  ensureInvite, getMyInvite, listMembers, removeMember, revokeInvite, rotateInvite,
} from '../../msg/inviteApi';
import { inviteUrl } from '../../msg/inviteLink';
import { logger } from '../../utils/logger';
import '../Settings/settings.css';
import './myQr.css';

const LABEL_MAX = 64;

/**
 * Период тихого обновления списка участников и счётчика использований.
 *
 * 🔴 Зачем (живой прогон 25.08): владелец показывает QR, человек сканирует и
 * входит — а страница до ручного F5 держит «Участники (0)» и «использован
 * 0 раз». Владелец при этом стоит рядом с гостем и решает, что вход не прошёл.
 * Ссылки «Чат» на нового участника тоже нет, то есть открыть с ним чат
 * (а именно там заводится устройство владельца — см. bootstrapError) нельзя.
 */
const MEMBERS_POLL_MS = 15_000;

/** Подпись как её проверит сервер: strip, 1..64, без управляющих символов. */
export function normalizeLabel(raw) {
  const label = String(raw ?? '').trim();
  // eslint-disable-next-line no-control-regex
  if (!label || label.length > LABEL_MAX || /[\x00-\x1f\x7f]/.test(label)) return null;
  return label;
}

/** Текст ошибки ручки для владельца — без технических потрохов и без токена. */
function errorText(e, fallback) {
  if (e?.status === 403) return 'Недостаточно прав.';
  if (e?.status === 404) return 'Раздел недоступен (функция выключена на сервере).';
  if (e?.status === 409) return 'Параллельное изменение — обновите страницу и повторите.';
  if (e?.status === 422) return 'Подпись: 1–64 символа, без управляющих символов.';
  return fallback;
}

const MyQr = () => {
  const [activeLink, setActiveLink] = useState(null);
  const { msgInvites, msgRooms, loaded } = useAuth();
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [invite, setInvite] = useState(null);
  const [members, setMembers] = useState([]);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  // Синхронный лок операций (ревью р.1, Medium): disabled по busy защищает
  // только после rerender — двойной клик/Enter по «Да» до него отправил бы
  // два rotate (= два новых токена подряд) или два DELETE.
  const busyRef = useRef(false);
  // Поколение состояния: любая операция владельца (create/rotate/revoke/
  // remove) его сдвигает. 🔴 Тихое обновление, УШЕДШЕЕ до операции, обязано
  // выбросить свой ответ: иначе поздний список вернёт удалённого участника, а
  // поздний getMyInvite — отозванный токен, и в поле/QR всплывёт мёртвая ссылка.
  const genRef = useRef(0);
  // 🔴 Поле подписи правили РУКАМИ? (ревью Codex 25.08 р.2, Medium-1). Раньше
  // «правили» выводилось сравнением поля с invite.label — и это ломалось, как
  // только invite стал приезжать сам: rotate в соседней вкладке приносил новую
  // подпись, поле оставалось со старой, и следующий «Выпустить новый» ТИХО
  // откатывал подпись на прежнюю, хотя владелец её здесь не трогал.
  const labelDirtyRef = useRef(false);
  // 🔴 Номер обновления: побеждает ПОСЛЕДНЕЕ ушедшее, а не последнее
  // вернувшееся (ревью Codex 25.08, Medium-2). Опрос и ручное «Обновить» летят
  // параллельно, и без этого счётчика ответ старого опроса, пришедший вторым,
  // откатил бы экран — только что появившийся участник «пропал» бы у владельца
  // на глазах. Поколение genRef этого не ловит: оно у обоих одинаковое.
  const refreshSeqRef = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(null); // 'rotate' | 'revoke' | `member:<id>`

  useEffect(() => {
    setActiveLink(
      `${window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1)}`
    );
  }, []);

  const reload = useCallback(async () => {
    try {
      const [inv, list] = await Promise.all([getMyInvite(), listMembers()]);
      setInvite(inv);
      setMembers(list);
      if (inv) { setLabel(inv.label); labelDirtyRef.current = false; }
      setPhase('ready');
    } catch (e) {
      logger.error('Мой QR: не удалось загрузить приглашение/участников', e);
      setError(errorText(e, 'Не удалось загрузить данные. Обновите страницу.'));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (!loaded || !msgInvites) return;
    reload();
  }, [loaded, msgInvites, reload]);

  /**
   * Тихое обновление приглашения и участников. От reload отличается тем, что
   * 🔴 НЕ трогает поле подписи, ПОКА его правят руками (переписать ввод под
   * пальцами нельзя), и не переводит страницу в phase='error': сорвавшийся
   * фоновый опрос не повод рушить уже показанный экран.
   */
  const refresh = useCallback(async ({ manual = false } = {}) => {
    if (busyRef.current) return; // идёт rotate/revoke/удаление — не мешаем
    const gen = genRef.current;
    refreshSeqRef.current += 1;
    const seq = refreshSeqRef.current;
    /** Владелец успел сделать что-то своё, пока ответ летел. */
    const ownerMoved = () => gen !== genRef.current || busyRef.current;
    if (manual) { setRefreshing(true); setError(null); setNotice(null); }
    try {
      const [inv, list] = await Promise.all([getMyInvite(), listMembers()]);
      // 🔴 Свежесть ДАННЫХ и обратная связь РУЧНОГО действия — разные вопросы
      // (ревью р.3, Medium). Общий счётчик отвечал на оба сразу, и ручное
      // «Обновить», которое висело дольше тика опроса, проваливалось молча:
      // ни «обновлено», ни ошибки — явное действие владельца без ответа.
      // Данные: применяем, только если владелец ничего не делал И нас не
      // обогнало более свежее обновление (его снимок новее нашего).
      if (!ownerMoved() && seq === refreshSeqRef.current) {
        setInvite(inv);
        setMembers(list);
        // Поле не трогали -> держим его в согласии с сервером: иначе rotate
        // отправит подпись, которой на сервере давно нет (Medium-1).
        if (inv && !labelDirtyRef.current) setLabel(inv.label);
      }
      // Ответ человеку: молчим только если владелец успел сделать что-то своё —
      // поверх его «Приглашение отозвано» наше «обновлено» было бы шумом.
      if (manual && !ownerMoved()) setNotice('Список обновлён.');
    } catch (e) {
      // Это наш logger, а не screen.debug: фоновый опрос падает молча по
      // замыслу — правило testing-library срабатывает по имени метода.
      // eslint-disable-next-line testing-library/no-debugging-utils
      logger.debug('Мой QR: тихое обновление не удалось', e);
      // 🔴 Молчим, только если владелец успел сделать что-то своё (ревью р.2,
      // Medium-2): иначе поздний сбой напишет «Не удалось обновить» поверх
      // успешного «Приглашение отозвано». Про номер обновления здесь НЕ
      // спрашиваем (ревью р.3): фоновый тик не имеет права съесть ответ на
      // явное действие человека.
      if (manual && !ownerMoved()) setError(errorText(e, 'Не удалось обновить — проверьте связь.'));
    } finally {
      // 🔴 Крутилку гасит ТОТ, КТО ЕЁ ЗАЖЁГ — без всяких проверок актуальности.
      // Ревью р.2 предлагало охранять и эту ветку номером обновления, но номер
      // общий с фоновым опросом: тик в 15 с сдвигает его, ответ ручного
      // обновления перестаёт гасить свою же крутилку — и кнопка «Обновляем…»
      // навсегда, притом disabled. Гонки тут нет: пока refreshing, кнопка
      // недоступна, значит второе РУЧНОЕ обновление начаться не может.
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (phase !== 'ready') return undefined;
    // Во вкладке-невидимке не опрашиваем (владелец ушёл в другое окно), но
    // возврат в неё — самый частый момент «ну что, вошёл?»: обновляем сразу.
    const tick = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const timer = setInterval(tick, MEMBERS_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [phase, refresh]);

  const url = invite ? (invite.url ?? inviteUrl(window.location.origin, invite.token)) : null;

  useEffect(() => {
    // QR перерисовывается на КАЖДУЮ смену ссылки (rotate -> новый токен)
    let cancelled = false;
    setQrError(false);
    // Старую картинку снимаем СРАЗУ (ревью р.2, Low): после rotate ссылка в
    // поле уже новая, а QR/«Скачать» старого, отозванного, висеть не должны
    setQrDataUrl(null);
    if (!url) return undefined;
    QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 2, width: 320 })
      .then((d) => { if (!cancelled) setQrDataUrl(d); })
      .catch((e) => {
        // Ревью р.1, Low: отказ рендера не должен выглядеть как вечное
        // «готовится» — ссылка при этом рабочая, её можно отдать руками
        logger.error('Мой QR: QR не отрисовался', e);
        if (!cancelled) { setQrDataUrl(null); setQrError(true); }
      });
    return () => { cancelled = true; };
  }, [url]);

  const run = async (fn, okText) => {
    if (busyRef.current) return;
    busyRef.current = true;
    genRef.current += 1; // ответы тихих опросов, ушедших раньше, — за борт
    setBusy(true);
    setError(null);
    setNotice(null);
    setConfirming(null);
    try {
      await fn();
      if (okText) setNotice(okText);
    } catch (e) {
      logger.error('Мой QR: операция не удалась', e);
      setError(errorText(e, 'Не получилось — попробуйте ещё раз.'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const create = () => {
    const clean = normalizeLabel(label);
    if (!clean) { setError('Подпись: 1–64 символа, без управляющих символов.'); return; }
    run(async () => {
      const inv = await ensureInvite(clean);
      setInvite(inv);
      setLabel(inv.label);
      labelDirtyRef.current = false;
    }, 'QR создан. Покажите его собеседнику — он отсканирует из приложения.');
  };

  const rotate = () => {
    // Подпись: поле не трогали -> null (сервер наследует прежнюю); трогали ->
    // ОБЯЗАНА пройти валидацию ДО запроса (ревью р.1, Medium: пустая/битая
    // правка не должна молча отзывать старый QR с унаследованной подписью).
    // 🔴 Именно флаг правки, а не сравнение со снимком invite: снимок теперь
    // обновляется сам, и после чужого rotate сравнение назвало бы «правкой»
    // подпись, которую здесь никто не трогал (ревью р.2, Medium-1).
    // Честно: пока опрос подтягивает подпись в нетронутое поле, сравнение дало
    // бы тот же ответ — мутант «вернуть сравнение» выживает и это эквивалент.
    // Флаг оставлен как источник истины: он не зависит от того, успел ли опрос.
    const changed = labelDirtyRef.current;
    const clean = changed ? normalizeLabel(label) : null;
    if (changed && !clean) {
      setConfirming(null);
      setError('Подпись: 1–64 символа, без управляющих символов.');
      return;
    }
    run(async () => {
      const inv = await rotateInvite(clean);
      setInvite(inv);
      setLabel(inv.label);
      labelDirtyRef.current = false;
    }, 'Выпущен новый QR. Старый больше не работает, участники остались.');
  };

  const revoke = () => run(async () => {
    await revokeInvite();
    setInvite(null);
  }, 'Приглашение отозвано. Участники остались — их можно удалить ниже.');

  const remove = (m) => run(async () => {
    await removeMember(m.id);
    setMembers((prev) => prev.filter((x) => x.id !== m.id));
  }, `Участник «${m.display_name}» удалён.`);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice('Ссылка скопирована.');
    } catch (e) {
      setNotice('Скопируйте ссылку вручную из поля ниже.');
    }
  };

  // «1 новое», но «2 новых». 🔴 При обрезке до «99+» форму берём МНОЖЕСТВЕННУЮ,
  // а не по настоящему числу (ревью Codex): 101 давало бы «99+ новое».
  const unreadLabel = (n) => (n > 99
    ? '99+ новых'
    : `${n} ${(n % 10 === 1 && n % 100 !== 11) ? 'новое' : 'новых'}`);

  const fmtDate = (epochSec) => (
    Number.isFinite(epochSec) ? new Date(epochSec * 1000).toLocaleString() : ''
  );

  const confirmRow = (key, text, onYes) => (
    confirming === key ? (
      <span className="qrConfirm" data-testid={`confirm-${key}`}>
        <span>{text}</span>
        <button type="button" disabled={busy} onClick={onYes}>Да</button>
        <button type="button" disabled={busy} onClick={() => setConfirming(null)}>Отмена</button>
      </span>
    ) : null
  );

  const body = () => {
    if (!loaded) return <p className="qrNote">Загрузка…</p>;
    if (!msgInvites) return <p className="qrNote">Раздел недоступен.</p>;
    if (phase === 'loading') return <p className="qrNote">Загрузка…</p>;
    if (phase === 'error') return <p className="qrNote qrError">{error}</p>;
    return (
      <>
        <section className="qrSection">
          <h3 className="qrTitle">Мой QR — «чат со мной»</h3>
          <p className="qrHelp">
            Собеседник наводит камеру на QR, вводит своё имя и сразу попадает в
            защищённый чат с вами — прямо в браузере, на iPhone и Android,
            устанавливать ничего не нужно. Один активный QR на владельца.
          </p>
          {/* Порядок «сначала владелец» — требование крипты, а не сценария:
              ключ владельца заводится при открытии ЧАТА, эта страница его не
              создаёт. Пока чат не открыт, гость честно видит «собеседник ещё
              не открыл чат» (bootstrapError). Живой прогон 25.08. */}
          <p className="qrHelp">
            После того как человек вошёл, откройте с ним чат кнопкой «Чат» ниже:
            ключ шифрования заводится именно там, и до этого собеседник будет
            видеть «собеседник ещё не открыл чат».
          </p>
          <label className="qrField">
            <span>Подпись (её увидит приглашённый: «Вас приглашает …»)</span>
            <input
              type="text"
              value={label}
              maxLength={LABEL_MAX}
              placeholder="Например: Олег, Ascom"
              disabled={busy}
              onChange={(e) => { labelDirtyRef.current = true; setLabel(e.target.value); }}
              data-testid="qr-label"
            />
          </label>
          {!invite ? (
            <button type="button" className="qrPrimary" disabled={busy} onClick={create}>
              Создать QR
            </button>
          ) : (
            <div className="qrCard" data-testid="qr-card">
              {qrDataUrl && (
                <img className="qrImage" src={qrDataUrl} alt="QR-код приглашения" data-testid="qr-image" />
              )}
              {!qrDataUrl && !qrError && <p className="qrNote">QR готовится…</p>}
              {qrError && (
                <p className="qrNote qrError" role="alert" data-testid="qr-render-error">
                  Не удалось отрисовать QR — скопируйте ссылку ниже и отправьте её собеседнику.
                </p>
              )}
              <p className="qrMeta">
                Подпись: <b>{invite.label}</b>
                {' · '}
                использован {invite.use_count} раз
                {' · '}
                создан {fmtDate(invite.created_at)}
              </p>
              <input
                className="qrLink"
                type="text"
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                data-testid="qr-url"
              />
              <div className="qrActions">
                <button type="button" disabled={busy} onClick={copyLink}>Скопировать ссылку</button>
                {qrDataUrl && (
                  <a className="qrBtnLink" href={qrDataUrl} download="total-invite-qr.png" data-testid="qr-download">
                    Скачать PNG
                  </a>
                )}
                <button type="button" disabled={busy} onClick={() => setConfirming('rotate')}>
                  Выпустить новый
                </button>
                <button type="button" className="qrDanger" disabled={busy} onClick={() => setConfirming('revoke')}>
                  Отозвать
                </button>
              </div>
              {confirmRow('rotate', 'Старый QR перестанет работать, участники останутся. Выпустить новый?', rotate)}
              {confirmRow('revoke', 'QR перестанет работать, участники останутся. Отозвать?', revoke)}
            </div>
          )}
        </section>

        {/* QR-2 В2: вход в общую комнату. Показывается ТОЛЬКО при msgRooms из
            /me — при выключенном флаге поверхности комнаты нет вовсе, и
            ссылка вела бы человека в 404. Стоит НАД списком участников: это
            разговор со всеми сразу, а ниже — переписка с каждым по отдельности. */}
        {msgRooms && (
          <section className="qrSection" data-testid="qr-room-entry">
            <h3 className="qrTitle">Общий чат</h3>
            <p className="qrHelp">
              Одна переписка на всех, кто вошёл по вашей ссылке: пишут все и
              видят все. Личные чаты с каждым остаются ниже.
            </p>
            <Link className="qrBtnLink" to="/msg/room">Открыть общий чат</Link>
          </section>
        )}
        <section className="qrSection">
          <h3 className="qrTitle">
            Участники ({members.length})
            {' '}
            <button
              type="button"
              className="qrRefresh"
              disabled={busy || refreshing}
              onClick={() => refresh({ manual: true })}
              data-testid="qr-refresh"
            >
              {refreshing ? 'Обновляем…' : 'Обновить'}
            </button>
          </h3>
          {members.length === 0 ? (
            <p className="qrNote">
              Пока никто не присоединился. Список обновляется сам — держите
              страницу открытой, пока собеседник сканирует QR.
            </p>
          ) : (
            <ul className="qrMembers">
              {members.map((m) => (
                <li key={m.id} className="qrMember" data-testid={`member-${m.id}`}>
                  <span className="qrMemberName">{m.display_name}</span>
                  <span className="qrMemberDate">{fmtDate(m.created_at)}</span>
                  {/* WEB-19 §4.2 (Д-2): без активного устройства строка — не
                      собеседник: человек вошёл по ссылке, но чат на телефоне не
                      открывал (или устройство отозвано) — сообщения ему не
                      дойдут. Старый сервер поля не отдаёт — значка нет. */}
                  {m.has_device === false && (
                    <span
                      className="qrMemberPending"
                      data-testid={`member-nodevice-${m.id}`}
                      title="Человек вошёл по ссылке, но чат на его устройстве ещё не открывался — сообщения ему не дойдут. Пусть откроет чат на том же телефоне."
                    >
                      вход не завершён
                    </span>
                  )}
                  {/* 🔴 25.08 на бою участники написали владельцу пять сообщений,
                      и список выглядел так же, как если бы никто не писал —
                      он о них не узнал. Счётчик приходит с сервера (метаданные,
                      не крипта) и стоит РЯДОМ с кнопкой «Чат», а не в углу. */}
                  {m.unread > 0 && (
                    <span
                      className="qrMemberUnread"
                      data-testid={`member-unread-${m.id}`}
                      title="Непрочитанные сообщения"
                    >
                      {unreadLabel(m.unread)}
                    </span>
                  )}
                  <Link className="qrBtnLink" to={`/msg/m/${m.id}`}>Чат</Link>
                  <button
                    type="button"
                    className="qrDanger"
                    disabled={busy}
                    onClick={() => setConfirming(`member:${m.id}`)}
                  >
                    Удалить
                  </button>
                  {confirmRow(
                    `member:${m.id}`,
                    `Удалить участника «${m.display_name}»? Переписка будет стёрта, доступ пропадёт.`,
                    () => remove(m),
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {error && <p className="qrNote qrError" role="alert">{error}</p>}
        {notice && <p className="qrNote qrNotice" role="status">{notice}</p>}
      </>
    );
  };

  return (
    <CabinetShell title="Мой QR" floor={false}>
      <SettingsNav activeLink={activeLink} setActiveLink={setActiveLink} />
      <div className="settingsContent">
        <div className="MyQr">{body()}</div>
      </div>
    </CabinetShell>
  );
};

export default MyQr;
